import { useState } from "react";
import { AuditLogItem } from "@/api/emails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ChevronRight, Clock } from "lucide-react";

export type AuditEvent = AuditLogItem & { email_subject?: string | null };
const stepLabels: Record<string, string> = {
  spam_check: "Controllo spam",
  classification: "Classificazione",
  entity_resolution: "Paziente e proprietario",
  case_matching: "Collegamento al caso",
  routing: "Assegnazione",
  auto_reply: "Risposta automatica",
  imaging: "Analisi immagini",
};
export function groupAuditEvents(logs: AuditEvent[]) {
  const groups = new Map<
    string,
    { id: string; title: string; logs: AuditEvent[] }
  >();
  const sorted = [...logs].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  for (const log of sorted) {
    const id = log.email_id || "case-events";
    if (!groups.has(id))
      groups.set(id, {
        id,
        title:
          log.email_subject || (log.email_id ? "Messaggio" : "Eventi del caso"),
        logs: [],
      });
    groups.get(id)!.logs.push(log);
  }
  return Array.from(groups.values());
}
const fieldLabels: Record<string, string> = {
  category: "Categoria",
  priority: "Priorità",
  specialty: "Specialità",
  confidence: "Confidenza",
  patient_name: "Paziente",
  owner_name: "Proprietario",
  vet_name: "Veterinario",
  status: "Stato",
  tags: "Etichette",
  reasoning: "Motivazione",
  summary: "Riepilogo",
  summary_ai: "Riepilogo AI",
  decision: "Decisione",
  error: "Errore",
  message: "Messaggio",
  requires_human_review: "Richiede revisione umana",
  needs_patient_confirmation: "Richiede conferma paziente",
};
function RecordedData({
  value,
  raw = false,
  depth = 0,
}: {
  value: unknown;
  raw?: boolean;
  depth?: number;
}) {
  if (value == null)
    return <p className="text-muted-foreground">Non registrato</p>;
  if (typeof value === "boolean" || typeof value === "number")
    return (
      <span>
        {typeof value === "boolean" ? (value ? "Sì" : "No") : String(value)}
      </span>
    );
  if (typeof value === "string")
    return <p className="whitespace-pre-wrap break-words">{value}</p>;
  if (!raw && typeof value === "object" && depth < 3)
    return (
      <dl className="audit-data">
        {Object.entries(value).map(([key, item]) => (
          <div key={key}>
            <dt>{fieldLabels[key] || key.replace(/_/g, " ")}</dt>
            <dd>
              <RecordedData value={item} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  return <pre className="audit-code">{JSON.stringify(value, null, 2)}</pre>;
}
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string | null;
  logs: AuditEvent[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}
export function AuditTrailView({
  open,
  onOpenChange,
  title,
  subtitle,
  logs,
  loading,
  error,
  onRetry,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [step, setStep] = useState("all");
  const [emailId, setEmailId] = useState("all");
  const [mobileDetail, setMobileDetail] = useState(false);
  const groups = groupAuditEvents(logs);
  const filtered = groups
    .filter((group) => emailId === "all" || emailId === group.id)
    .map((group) => ({
      ...group,
      logs: group.logs.filter(
        (log) => step === "all" || log.step_name === step,
      ),
    }))
    .filter((group) => group.logs.length);
  const visible = filtered.flatMap((group) => group.logs);
  const selected =
    visible.find((log) => log.id === selectedId) || visible[visible.length - 1];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="audit-window"
      >
        <DialogHeader className="audit-header">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {subtitle ||
              "Cronologia delle elaborazioni e delle decisioni registrate"}
          </DialogDescription>
        </DialogHeader>
        <div className="audit-summary">
          <span>
            <strong>{logs.length}</strong> eventi registrati
          </span>
          <span>
            <strong>
              {groups.filter((group) => group.id !== "case-events").length}
            </strong>{" "}
            messaggi elaborati
          </span>
          <span>Decisioni e dati originali consultabili</span>
        </div>
        <div className={`audit-body ${mobileDetail ? "show-event" : ""}`}>
          <section className="audit-events" aria-label="Eventi audit">
            <div className="audit-filters">
              <label>
                Messaggio
                <select
                  value={emailId}
                  onChange={(event) => {
                    setEmailId(event.target.value);
                    setMobileDetail(false);
                  }}
                >
                  <option value="all">Tutti i messaggi</option>
                  {groups.map((group, index) => (
                    <option key={group.id} value={group.id}>
                      {index + 1}. {group.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Elaborazione
                <select
                  value={step}
                  onChange={(event) => {
                    setStep(event.target.value);
                    setMobileDetail(false);
                  }}
                >
                  <option value="all">Tutte le elaborazioni</option>
                  {Array.from(new Set(logs.map((log) => log.step_name))).map(
                    (name) => (
                      <option key={name} value={name}>
                        {stepLabels[name] || name.replace(/_/g, " ")}
                      </option>
                    ),
                  )}
                </select>
              </label>
            </div>
            {loading ? (
              <p className="mail-empty" role="status">
                Caricamento audit trail…
              </p>
            ) : error ? (
              <div className="mail-empty" role="alert">
                <p>Impossibile caricare la cronologia</p>
                <Button variant="outline" onClick={onRetry}>
                  Riprova
                </Button>
              </div>
            ) : visible.length === 0 ? (
              <p className="mail-empty">
                Nessun evento disponibile per questa selezione
              </p>
            ) : (
              filtered.map((group, index) => (
                <div key={group.id}>
                  <h3 className="audit-group-title" title={group.title}>
                    {index + 1}. {group.title}{" "}
                    <span>({group.logs.length})</span>
                  </h3>
                  {group.logs.map((log) => (
                    <button
                      key={log.id}
                      className={`audit-event ${selected?.id === log.id ? "is-selected" : ""}`}
                      aria-current={
                        selected?.id === log.id ? "true" : undefined
                      }
                      onClick={() => {
                        setSelectedId(log.id);
                        setMobileDetail(true);
                      }}
                    >
                      <span className="flex justify-between gap-2">
                        <strong>
                          {stepLabels[log.step_name] ||
                            log.step_name.replace(/_/g, " ")}
                        </strong>
                        <ChevronRight className="size-3.5 shrink-0" />
                      </span>
                      <time>
                        <Clock className="size-3 inline mr-1" />
                        {new Date(log.timestamp).toLocaleString("it-IT")}
                      </time>
                      <span className="line-clamp-2">
                        {log.decision || "Apri i dati registrati"}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </section>
          <section className="audit-event-detail" aria-label="Dettaglio evento">
            <Button
              className="audit-back"
              variant="ghost"
              onClick={() => setMobileDetail(false)}
            >
              <ArrowLeft className="size-4" />
              Eventi
            </Button>
            {!loading && !error && selected ? (
              <>
                <h2>
                  {stepLabels[selected.step_name] ||
                    selected.step_name.replace(/_/g, " ")}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(selected.timestamp).toLocaleString("it-IT")}
                  {selected.email_subject ? ` · ${selected.email_subject}` : ""}
                </p>
                <div className="audit-decision">
                  <h3>Decisione registrata</h3>
                  <p>
                    {selected.decision ||
                      "Nessuna decisione testuale registrata per questo evento."}
                  </p>
                </div>
                <h3 className="mb-3 font-semibold">Risultato</h3>
                <RecordedData value={selected.output_data} />
                <details key={selected.id} className="audit-technical">
                  <summary>Dettagli tecnici originali</summary>
                  {[
                    ["Input", selected.input_data],
                    ["Prompt", selected.llm_prompt],
                    ["Risposta del modello", selected.llm_response],
                    ["Metadati", selected.extra_data],
                  ].map(([label, value]) => (
                    <section key={label as string}>
                      <h4>{label as string}</h4>
                      <RecordedData value={value} raw />
                    </section>
                  ))}
                  <section>
                    <h4>Identificativi</h4>
                    <RecordedData
                      value={{
                        event_id: selected.id,
                        email_id: selected.email_id,
                      }}
                    />
                  </section>
                </details>
              </>
            ) : (
              <p className="text-muted-foreground">
                {error
                  ? "La cronologia non è disponibile."
                  : "Seleziona un evento per consultarne la decisione."}
              </p>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
